import type { CSSProperties, JSX } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import type { User } from '@auth0/auth0-react';
import { Menu, MenuItem } from 'carbon-react/esm/components/menu';
import Box from 'carbon-react/esm/components/box';
import GlobalHeader from 'carbon-react/esm/components/global-header';
import Divider from 'carbon-react/esm/components/divider';
import Profile from 'carbon-react/esm/components/profile';
import { useAuthAppContext } from '../context/useAuthAppContext';
import Typography from 'carbon-react/esm/components/typography';
import { AppRoutes } from '../types/appRoutes';
import { useUserInfos } from '../hooks/useUserInfos';
import MenuItemWithReactRouter from './MenuItemWithReactRouter';
import { ENV } from '../config/env';

const Logo = () => <img src="/assets/sage-logo.svg" alt="Sage" width="40px" />;

export const LogoutButton = (): JSX.Element => {
  const { handleLogout } = useUserInfos();

  return <MenuItem onClick={handleLogout}>Déconnexion</MenuItem>;
};

interface UserMenuTriggerProps {
  user: User | null | undefined;
}

const UserMenuTrigger = ({ user }: UserMenuTriggerProps): JSX.Element => {
  const firstName = user?.given_name ?? '';
  const lastName = user?.family_name ?? '';
  const fullName = `${firstName} ${lastName}`.trim();
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

  return (
    <Box display="flex" alignItems="center" gap={2}>
      <div
        style={
          {
            '--profile-size-outside-s': '24px',
            '--profile-size-inside-s': '15px',
            '--profile-font-initials-s': '11px',
          } as CSSProperties
        }
      >
        <Profile
          src={user?.picture}
          alt={fullName || 'Profil utilisateur'}
          initials={initials}
          size="S"
          variant="gray"
        />
      </div>
      <Typography variant="b" inverse>
        {firstName}
      </Typography>
    </Box>
  );
};

const MainNavigationBarComponent = (): JSX.Element => {
  const { user: auth0User } = useAuth0();
  const { isAuthenticated, authMethod, keyUser } = useAuthAppContext();
  const environment = ENV.APP_ENV;
  const showEnvironment = environment && environment !== 'Production';
  const user = authMethod === 'key' ? keyUser : auth0User;

  return (
    <Box key="divider" display="flex" mt={40}>
      <GlobalHeader pr={2}>
        <Logo />
        <Divider pt={1} pb={1} pl={2} pr={0} height={24} />
        <Typography variant="b" inverse pl={2} pt={1}>
          EDI Online{showEnvironment && ` [${environment}]`}
        </Typography>

        <Menu menuType="black" flex="1" justifyContent="flex-end">
          {isAuthenticated ? (
            <MenuItem submenu={<UserMenuTrigger user={user} />} clickToOpen>
              <MenuItemWithReactRouter to={AppRoutes.routeUserAccount}>
                Mon compte
              </MenuItemWithReactRouter>
              <LogoutButton />
            </MenuItem>
          ) : (
            <MenuItemWithReactRouter to={AppRoutes.routeLogin}>
              Se connecter
            </MenuItemWithReactRouter>
          )}
          <MenuItem submenu="Aide" submenuDirection="left" clickToOpen>
            <MenuItem href="#">Centre d&apos;aide</MenuItem>
            <MenuItem href="#">Donnez-nous votre opinion</MenuItem>
          </MenuItem>
        </Menu>
      </GlobalHeader>
    </Box>
  );
};

export default MainNavigationBarComponent;
